import { Injectable } from '@angular/core';
import {
  Database,
  ref,
  objectVal,
  listVal,
  query,
  orderByKey,
  endAt,
  limitToLast,
  update,
  remove,
} from '@angular/fire/database';
import { ConnectionServices } from 'app/services/authentication/connection/connection.service';
import { RolesEnum } from 'app/services/authentication/identity.service';
import { GetAllChatCommand, IGetAllChatCommandParameters } from 'app/services/carrier/command/getAllChat.command';
import { GetAllChatContentCommand, IGetAllChatContentCommandParameters } from 'app/services/carrier/command/getAllChatContent.command';
import { ISendChatMessageCommandParameter, SendChatMessageCommand } from 'app/services/carrier/command/sendChatMessage.command';
import { ISendVoiceChatMessageCommandParameter, SendVoiceChatMessageCommand } from 'app/services/carrier/command/sendVoiceChatMessage.command';
import { Observable, throwError, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';


@Injectable({ providedIn: 'root' })
export class ChatNotificationsService {
  showHttpErrorMessage(arg0: string, error: any) {
    throw new Error('Method not implemented.');
  }
  /** Mantengo la semántica del servicio viejo: generar la URL según rol y userId */
  private generateUrl(userId: string | number, userRole: RolesEnum): string {
    let url = `notifications_carrier/${userId}/`;
    switch (userRole) {
      case RolesEnum.ROLE_CARRIER:
        url += 'carrier';
        break;
      case RolesEnum.ROLE_CARRIER_OPERATOR:
        url += 'carrier_operator';
        break;
      case RolesEnum.ROLE_CARRIER_MANAGER:
        url += 'carrier_manager';
        break;
      default:
        url += 'carrier';
        break;
    }
    return url;
  }

  constructor(
    private db: Database,
    private _connectionService: ConnectionServices
  ) { }

  // ===========================
  // RTDB: listas y objetos
  // ===========================

  /**
   * Equivalente al viejo getAll(limit, userId, userRole, lastKey?)
   * Devuelve la lista como observable (listVal) ordenada por key, con paginación por lastKey.
   */
  public getAll(
    limit: number,
    userId: string | number,
    userRole: RolesEnum,
    lastKey?: string
  ): Observable<any[]> {
    const url = this.generateUrl(userId, userRole);
    const q = lastKey
      ? query(ref(this.db, url), orderByKey(), endAt(lastKey), limitToLast(limit))
      : query(ref(this.db, url), orderByKey(), limitToLast(limit));

    // listVal emite un array con los valores de la lista (sin las keys)
    return listVal<any>(q);
  }

  /**
   * Cambiar estado (equivalente al viejo changeStatus)
   * Antes: item.update(obj.key, { ... })
   * Ahora: update(ref(db, `${url}/${obj.key}`), { ... })
   */
  public changeStatus(
    obj: { key: string },
    userId: string | number,
    subUserId: string,
    userRole: RolesEnum,
    userName: string,
    status: boolean
  ): Observable<void> {
    const url = this.generateUrl(userId, userRole);
    const itemRef = ref(this.db, `${url}/${obj.key}`);

    const checker = status
      ? { subUserId, subUserName: userName }
      : { subUserId: '', subUserName: '' };

    const unchecker = status
      ? { subUserId: '', subUserName: '' }
      : { subUserId, subUserName: userName };

    const reading_date = status ? Date.now() : 0;

    return from(
      update(itemRef, { status, checker, unchecker, reading_date })
    );
  }

  /** NODO: chats no leídos (antes: this.afDB.object(...).valueChanges()) */
  getChatsNoReading(idCarrier: string | number) {
    return objectVal<Record<string, number>>(
      ref(this.db, `notifications_carrier/${idCarrier}/notifications/chats`)
    ); // Observable<Record<string, number> | null>
  }

  /** Import files */
  public getImportFilesById(idCarrier: string | number, id: string | number) {
    return objectVal<any>(ref(this.db, `import_files/${idCarrier}/travels/${id}`));
  }

  public deletegetImportFilesById(idCarrier: string | number, id: string | number) {
    return from(remove(ref(this.db, `import_files/${idCarrier}/travels/${id}`)));
  }

  public getImportFiles(idCarrier: string | number) {
    return objectVal<any>(ref(this.db, `import_files/${idCarrier}`));
  }

  // ===========================
  // Backend (REST/commands)
  // ===========================

  public getAllChats(idCarrier: string | number, role: string) {
    const command = new GetAllChatCommand();
    command.setParameters(<IGetAllChatCommandParameters>{
      carrierId: idCarrier,
      role,
    });

    const isMessageApi = true;
    return this._connectionService.Request(command, isMessageApi).pipe(
      map((data) => data),
      catchError((error) => throwError(() => error))
    );
  }

  public getAllChatContent(role: string, carrierId: string | number, driverId: string | number) {
    const command = new GetAllChatContentCommand();
    command.setParameters(<IGetAllChatContentCommandParameters>{
      role,
      carrierId,
      driverId,
    });

    const isMessageApi = true;
    return this._connectionService.Request(command, isMessageApi).pipe(
      map((data) => data),
      catchError((error) => throwError(() => error))
    );
  }

  public sendChatMessage(params: ISendChatMessageCommandParameter) {
    const command = new SendChatMessageCommand();
    command.setParameters(params);

    const isMessageApi = true;
    return this._connectionService.Request(command, isMessageApi).pipe(
      map((data) => data),
      catchError((error) => throwError(() => error))
    );
  }

  public sendVoiceChatMessage(params: ISendVoiceChatMessageCommandParameter) {
    const command = new SendVoiceChatMessageCommand();
    command.setParameters(params);

    const isMessageApi = true;
    return this._connectionService.Request(command, isMessageApi).pipe(
      map((data) => data),
      catchError((error) => throwError(() => error))
    );
  }




}
